<template>
  <v-container fluid>
    <MessagePopup :text="errorMessage" color="error"/>
    <v-container fluid>
      <v-row>
        <v-col cols="12" sm="8" md="4" class="pt-2">
          <v-text-field
            label="Part number"
            v-model="query"
            @keyup.enter="searchAll"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" md="3" class="pt-2">
          <v-switch
            label="Exact match"
            v-model="exact"
          ></v-switch>
        </v-col>
        <v-col cols="4" sm="3" md="2">
          <v-btn @click="searchAll" :loading="loading" class="mr-4" :disabled="query === ''">
            Search
          </v-btn>
        </v-col>
        <v-col cols="8" sm="8" md="3" class="pt-4">
          {{ items.length.toLocaleString() }} results found
          <span v-if="loadingSpeed">({{ loadingSpeed.toLocaleString(undefined, { maximumFractionDigits: 1 }) }}s)</span>
        </v-col>
      </v-row>
    </v-container>
    <ChartArticlesPrices :items="items"/>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :search="filter"
      show-expand
      :items-per-page="-1"
      hide-default-footer
      :sort-by="['stockCoversNeed', 'linePriceNormalized']"
      multi-sort
      class="elevation-4 mt-8"
    >
      <template v-slot:top>
        <v-container fluid>
          <v-row justify="end">
            <v-col cols="10" sm="6">
              <v-slider
                class="mt-4"
                thumb-label="always"
                min="0"
                max="20"
                v-model="quantityLog"
              >
                <template v-slot:thumb-label>
                  {{ quantityString }}
                </template>
              </v-slider>
            </v-col>
            <v-col cols="2" sm="2">
              <v-text-field
                type="number"
                class="mt-0 pt-0"
                v-model="quantity"
                label="Quantity"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                class="mt-0 pt-0"
                v-model="filter"
                append-icon="mdi-magnify"
                label="Filter"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template v-slot:item.sku="{ item }">
        <router-link :to="{ name: 'ArticlesDetail', params: { id: item.id } }">
          {{ item.sku }}
        </router-link>
      </template>
      <template v-slot:item.stockCoversNeed="{ item }">
        <v-chip :color="stockColor(item.update.stock)" :outlined="quantity > item.update.stock" class="black--text">
          {{ item.update.stock.toLocaleString() }}
        </v-chip>
      </template>
      <template v-slot:item.linePriceNormalized="{ item }">
        <v-chip v-if="item.linePriceNormalized" :color="priceColor(item.linePriceNormalized)">
          {{ item.linePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.update.currency, currencyDisplay: 'symbol', style: 'currency' }) }}
        </v-chip>
      </template>
      <template v-slot:item.match.matchPriority="{ item }">
        <MatchCircleIcon :partNumber="item.manufacturerPartNumber" :query="query"/>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-container>
            <v-row>
              <v-col cols="12" sm="4" md="2">
                <v-img
                  class="mx-auto"
                  max-height="200px"
                  max-width="200px"
                  contain
                  :src="item.picture"
                ></v-img>
                <v-switch v-model="item.autocheck" label="Autocheck" @change="updateItem(item['@id'], { autocheck: item.autocheck })"></v-switch>
              </v-col>
              <v-col cols="12" sm="8" md="4">
                <h3>Product details</h3>
                <table>
                  <tr>
                    <td>SKU</td>
                    <td>
                      <router-link :to="{ name: 'ArticlesDetail', params: { id: item.id } }">
                        {{ item.sku }}
                      </router-link>
                    </td>
                  </tr>
                  <tr><td>Manufacturer</td><td>{{ item.manufacturerName }}</td></tr>
                  <tr><td>Part Number</td><td>{{ item.manufacturerPartNumber }}</td></tr>
                  <tr><td>Description</td><td>{{ item.description }}</td></tr>
                  <tr><td>Packaging</td><td>{{ item.packaging }}</td></tr>
                  <tr><td>Product page</td><td><v-btn x-small outlined :href="item.link" target="_blank">open</v-btn></td></tr>
                </table>
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <SingleUpdateView
                  :target="item['@id']"
                />
              </v-col>
            </v-row>
          </v-container>
        </td>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import SingleUpdateView from '@/components/SingleUpdateView.vue'
import MessagePopup from '@/components/MessagePopup.vue'
import ChartArticlesPrices from '@/components/ChartArticlesPrices'
import { interpolateRdYlGn, interpolatePurples } from 'd3-scale-chromatic'
import { mapState } from 'vuex'
import { bestPriceFromUpdate, checkPartNumberMatching } from '@/helpers/prices'
import MatchCircleIcon from '@/components/MatchCircleIcon'

export default {
  data: () => ({
    loading: false,
    query: 'NE555DR',
    exact: true,
    items: [],
    totalItems: 0,
    headers: [
      { text: 'SKU', value: 'sku' },
      { text: 'Supplier', value: 'supplier.name' },
      { text: 'Manufacturer', value: 'manufacturerName' },
      { text: 'Part Number', value: 'manufacturerPartNumber' },
      { text: 'Packaging', value: 'packaging' },
      { text: 'Match', value: 'match.matchPriority' },
      { text: 'Stock', value: 'stockCoversNeed', align: 'right' },
      { text: 'Total cost', value: 'linePriceNormalized', align: 'right' },
      { text: 'Details', value: 'data-table-expand' }
    ],
    filter: '',
    errorMessage: '',
    loadingStart: null,
    loadingStop: null,
    quantity: 1,
    quantityLog: 0,
    priceRange: { min: 0, max: 0 },
    stockMax: 0
  }),
  computed: {
    ...mapState(['suppliers']),
    loadingSpeed () {
      if (!this.loadingStart || !this.loadingStop) {
        return null
      }
      return (this.loadingStop - this.loadingStart) / 1000
    },
    quantityString () {
      if (this.quantity < 1000) {
        return this.quantity.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      } else {
        return (this.quantity / 1000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + 'k'
      }
    }
  },
  components: {
    SingleUpdateView,
    MessagePopup,
    ChartArticlesPrices,
    MatchCircleIcon
  },
  methods: {
    searchSupplier (supplier) {
      return this.$http(this.$directory + `/suppliers/${supplier.id}/articles/search`, {
        params: {
          query: this.query,
          exact: this.exact
        }
      })
        .then(response => {
          if (response.status === 200) {
            response.data['hydra:member'].forEach(item => {
              const article = item.article
              delete item.article
              article.update = item
              article.match = checkPartNumberMatching(article.manufacturerPartNumber, this.query)
              this.items.push(article)
            })
          }
        })
        .catch(error => {
          if (!error.response) {
            this.errorMessage = `Supplier ${supplier.name}: ${error.message}`
          } else {
            this.errorMessage = `Supplier ${supplier.name}: ${error.response.data['hydra:description'].replace(/\n/g, '<br/>')}`
          }
        })
    },
    searchAll () {
      if (this.query === '') {
        return
      }
      this.items = []
      this.loading = true
      this.errorMessage = ''
      this.loadingStop = null
      this.loadingStart = new Date()
      const promisesArray = []
      this.suppliers
        .filter(supplier => supplier.enabled)
        .forEach(supplier => {
          promisesArray.push(this.searchSupplier(supplier))
        })
      Promise.all(promisesArray)
        .finally(() => {
          this.loadingStop = new Date()
          this.loading = false
        })
    },
    updateItem (target, data) {
      this.errorMessage = ''
      this.$http.patch(target,
        data,
        {
          headers: {
            'Content-type': 'application/merge-patch+json'
          }
        }
      )
        .catch(error => {
          if (!error.response) {
            this.errorMessage = error.message
          } else {
            this.errorMessage = error.response.data['hydra:description'].replace(/\n/g, '<br/>')
          }
        })
    },
    totalPrices () {
      let minLinePrice = 0
      let maxLinePrice = 0

      this.items.forEach((item, index) => {
        if (item.update.stock > this.stockMax) {
          this.stockMax = item.update.stock
        }

        this.$set(this.items[index], 'stockCoversNeed', item.update.stock >= this.quantity ? 'Enought on stock' : 'Not Enought on stock')

        const linePrice = bestPriceFromUpdate(item.update, this.quantity)
        this.$set(this.items[index], 'linePrice', linePrice.linePrice)
        const linePriceNormalized = linePrice.linePrice * item.supplier.exchangeRate
        this.$set(this.items[index], 'linePriceNormalized', linePriceNormalized)

        if (minLinePrice === 0 || linePriceNormalized < minLinePrice) {
          minLinePrice = linePriceNormalized
        }
        if (maxLinePrice === 0 || linePriceNormalized > maxLinePrice) {
          maxLinePrice = linePriceNormalized
        }
      })
      this.priceRange.min = minLinePrice
      this.priceRange.max = maxLinePrice
    },
    priceColor (linePriceNormalized) {
      if (this.priceRange.min === 0 || this.priceRange.max === 0 || this.priceRange.min === this.priceRange.max) {
        return ''
      }
      const percentage = (linePriceNormalized - this.priceRange.min) / (this.priceRange.max - this.priceRange.min)

      return interpolateRdYlGn(0.75 - (percentage / 2))
    },
    stockColor (stock) {
      if (this.stockMax === 0) {
        return ''
      }
      const percentage = stock / this.stockMax

      return interpolatePurples(0.25 + (percentage / 2))
    }
  },
  watch: {
    quantity () {
      if (this.quantity > 0) {
        this.totalPrices()
      }
    },
    quantityLog () {
      const units = [1, 2.5, 5, 7.5]
      const unit = units[this.quantityLog % units.length]
      const multiplicator = Math.floor(this.quantityLog / units.length)
      this.quantity = parseInt(unit * Math.pow(10, multiplicator))
    },
    items () {
      this.totalPrices()
    }
  }
}
</script>
